import React, { useEffect, useState } from 'react';
import '../../styles/Teaser.css';
import { useSport } from '../../context/SportContext.tsx';
import { MoonLoader } from "react-spinners";
import { formatNumbers } from '../../helpers/functions.tsx';
import { getScheduleDateRange, PREGAME_SCHEDULE_EXTRA_DAYS } from '../../helpers/scheduleDateRange.ts';
import { InfoModal } from './InfoModal.tsx';
import ImageWithPlaceholder from '../ImageWithPlaceholder.tsx';
import { getDownloadURL, getStorage, ref } from 'firebase/storage';

const STORAGE_PREFIX = "gs://jocksportsdev.appspot.com/";

const FOOTBALL_LOGO = "/images/leagues/football.svg";
const BASKETBALL_LOGO = "/images/leagues/basketball.svg";
const WNBA_LOGO = "/images/leagues/basketball-wnba.svg";

const FALLBACK_LEAGUE_LOGOS: Record<string, string> = {
    ncaaf: FOOTBALL_LOGO,
    nfl: FOOTBALL_LOGO,
    football: FOOTBALL_LOGO,
    nba: BASKETBALL_LOGO,
    ncaab: BASKETBALL_LOGO,
    cbb: BASKETBALL_LOGO,
    basketball: BASKETBALL_LOGO,
    basket: BASKETBALL_LOGO,
    wnba: WNBA_LOGO,
};

const leagueLogoKey = (name?: string) =>
    String(name ?? "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");

const definedLeagueLogo = (name?: string, sport?: string) => {
    const key = leagueLogoKey(name);
    if (FALLBACK_LEAGUE_LOGOS[key]) return FALLBACK_LEAGUE_LOGOS[key];
    const blob = `${key} ${leagueLogoKey(sport)}`;
    if (blob.includes("wnba")) return WNBA_LOGO;
    if (blob.includes("basket")) return BASKETBALL_LOGO;
    if (blob.includes("football") || blob.includes("ncaaf") || blob.includes("nfl")) return FOOTBALL_LOGO;
    return "";
};

const resolveStoredLogo = async (logo?: string) => {
    const value = String(logo ?? "").trim();
    if (!value) return "";
    if (!value.startsWith("gs://")) return value;
    try {
        return await getDownloadURL(ref(getStorage(), value.replace(STORAGE_PREFIX, "")));
    } catch {
        return "";
    }
};

const TEASER_LEAGUE_DETAILS: Record<string, string> = {
    ncaaf: "College Football",
    nfl: "Professional Football",
    nba: "Basketball",
    wnba: "Women's Basketball",
    ncaab: "College Basketball",
    nhl: "Hockey",
    mlb: "Baseball",
};

const teaserLeagueDetail = (league: { name?: string; sport?: string }) => {
    const key = String(league.name ?? "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
    return TEASER_LEAGUE_DETAILS[key] || league.sport || "";
};

/*Component to display the available sports options obtained from the database */
function TeaserMenu({ setShowFooter }) {

    const teaserOptions = {
        "3TeamTiesLose": {
            label: "3 Team Special (7 pts FB) - Ties Lose",
            points: 7,
            odds: [{ team: 3, payout: "-130" }]
        },
        "4TeamTiesLose": {
            label: "4 Team Special (10 pts FB) - Ties Lose",
            points: 10,
            odds: [{ team: 4, payout: "-140" }]
        },
        "classic": {
            label: "Classic (4 pts FB)",
            points: 4,
            odds: [
                { team: 2, payout: "-120" },
                { team: 3, payout: "+150" },
                { team: 4, payout: "+235" },
                { team: 5, payout: "+350" },
                { team: 6, payout: "+550" },
                { team: 7, payout: "+800" }
            ]
        },
        "classic0.5": {
            label: "Classic +.5 (4.5 pts FB)",
            points: 4.5,
            odds: [
                { team: 2, payout: "-130" },
                { team: 3, payout: "+135" },
                { team: 4, payout: "+215" },
                { team: 5, payout: "+320" },
                { team: 6, payout: "+500" },
                { team: 7, payout: "+700" }
            ]
        },
        "classic1": {
            label: "Classic +1 (5 pts FB)",
            points: 5,
            odds: [
                { team: 2, payout: "-140" },
                { team: 3, payout: "+120" },
                { team: 4, payout: "+200" },
                { team: 5, payout: "+300" },
                { team: 6, payout: "+475" },
                { team: 7, payout: "+600" }
            ]
        }
    };
    const {
        sports,
        loadSports,
        loadTeaserGame,
        checkedTeaserLeagues,
        setSelectedGamesTeaser,
        errorMessages,
        showLimitAlert,
        handleCloseLimitAlert,
    } = useSport();
    const [selectedTeaser, setSelectedTeaser] = useState<{ key: string; points: number } | null>(null);
    const [oddsTeaser, setOddsTeaser] = useState<{ team: number; payout: string; label: string }[]>([]);
    const [teaserPoints, setTeaserPoints] = useState<number | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [showSports, setShowSports] = useState(true);
    const hasSelectedLeague = Object.values(checkedTeaserLeagues).some(v => v);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1000);

    useEffect(() => {
        const handleResize = () => {
            setIsSmallScreen(window.innerWidth < 1000);
        };

        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!isSmallScreen) {
            setSelectedGamesTeaser(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSmallScreen]);

    useEffect(() => {
        loadSports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const teaserLeagues = sports.flatMap((sport: any) =>
        (sport.leagues ?? [])
            .filter((league: any) => league.isActive && league.showInTeaser)
            .map((league: any) => ({
                ...league,
                sport: sport.name,
                sportLogo: sport.logo || "",
            }))
    );
    const [resolvedLogos, setResolvedLogos] = useState<Record<string, string>>({});

    useEffect(() => {
        let cancelled = false;

        const loadLogos = async () => {
            const entries = await Promise.all(teaserLeagues.map(async (league: any) => {
                const key = leagueLogoKey(league.name);
                const stored = await resolveStoredLogo(league.logo);
                const src = stored || definedLeagueLogo(league.name, league.sport) || await resolveStoredLogo(league.sportLogo);
                return [key, src] as const;
            }));
            if (!cancelled) setResolvedLogos(Object.fromEntries(entries));
        };

        loadLogos();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sports]);

    const handleTeaserClick = (key: any) => {
        if (isLoading) return;
        const selected = teaserOptions[key];
        setSelectedTeaser(key);
        setTeaserPoints(selected.points);
    };

    const handleOddsClick = (key: string) => {
        const selected = teaserOptions[key];
        const oddsWithLabel = selected.odds.map(odd => ({
            ...odd,
            label: selected.label
        }));
        setOddsTeaser(oddsWithLabel);
    };

    const handleTeaser = async (league: any) => {
        if (isLoading || !league || !teaserPoints) return;

        const teaserKey = `${league.name}-${teaserPoints}`;
        const isMain = true;

        // console.log(sport.name,)

        setIsLoading(true);
        try {
            const { startDateAfter, startDateBefore, timeOffset } = getScheduleDateRange({ extraDays: PREGAME_SCHEDULE_EXTRA_DAYS, league });
            await loadTeaserGame(
                league.sport,
                league,
                teaserPoints,
                teaserKey,
                selectedTeaser,
                isMain,
                startDateAfter,
                startDateBefore,
                timeOffset
            );

        } catch (error) {
            console.error("Error loading teaser game", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleNext = async () => {
        setShowFooter(false);

        if (window.innerWidth < 1000) {
            setShowSports(false);

        }
        setSelectedGamesTeaser(true)
    };

    return (
        <>
            {isLoading && isSmallScreen && (
                <div className="loading-overlay-menu">
                    <div className="loading-message">
                        <MoonLoader color="#b6cc18" loading={isLoading} size={55} />
                    </div>
                </div>
            )}
            {hasSelectedLeague && isSmallScreen && (
                <div className='containerButtonParlay'>
                    <button className='btn-custom' onClick={handleNext} disabled={isLoading}>
                        Continue
                    </button>
                </div>
            )}

            {showSports && (
                <div className="teaserMenu">
                    {!selectedTeaser ? (
                        <>
                            <div className="teaserTitleContent">
                                <span className="teaserTitle">Select your teaser below</span>
                            </div>
                            {Object.entries(teaserOptions).map(([key, teaser], index) => (
                                <div key={key} onClick={() => handleTeaserClick(key)} className={`clickable ${index % 2 === 0 ? 'even' : 'odd'}`}>
                                    <div>
                                        <span className="teaserBody">{teaser.label}</span>
                                    </div>
                                    <div className={`odds ${index % 2 === 0 ? 'even' : 'odd'}`} data-bs-toggle="modal" data-bs-target="#odds"
                                        onClick={(e) => { e.stopPropagation(); handleOddsClick(key); }}>
                                        <span>Odds</span>
                                    </div>
                                </div>
                            ))}
                        </>
                    ) : isSmallScreen ? (
                        <div className="teaser-sports">
                            <div className="teaser-sports__intro">
                                <h2 className="teaser-sports__title">Select a Sport</h2>
                                <p className="teaser-sports__subtitle">Choose the sport you want to place a teaser on.</p>
                            </div>
                            <div className="teaser-sports__list">
                                {teaserLeagues.map(league => {
                                    const leagueKey = `${league.name}-${teaserPoints}`;
                                    const isChecked = checkedTeaserLeagues[leagueKey] ?? false;
                                    const detail = teaserLeagueDetail(league);
                                    return (
                                        <button
                                            type="button"
                                            key={leagueKey}
                                            className={`teaser-sport${isChecked ? " is-selected" : ""}`}
                                            onClick={() => handleTeaser(league)}
                                        >
                                            <ImageWithPlaceholder
                                                src={resolvedLogos[leagueLogoKey(league.name)] || ""}
                                                className="teaser-sport__logo"
                                                placeholderClassName="teaser-sport__logo-placeholder"
                                                alt=""
                                            />
                                            <span className="teaser-sport__text">
                                                <span className="teaser-sport__name">{league.name}</span>
                                                {detail ? <span className="teaser-sport__detail">{detail}</span> : null}
                                            </span>
                                            <span className={`teaser-sport__radio${isChecked ? " is-selected" : ""}`} aria-hidden="true" />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div className="sportMenu">
                            <div className="teaserTitleContent">
                                <span className="teaserTitle">Select your sport below</span>
                            </div>
                            {teaserLeagues.map(league => {
                                const leagueKey = `${league.name}-${teaserPoints}`;
                                const isChecked = checkedTeaserLeagues[leagueKey] ?? false;
                                return (
                                    <div key={leagueKey} className={`teaser-League ${isChecked ? "active-league" : ""}`} style={{ cursor: "pointer" }}
                                        onClick={() => handleTeaser(league)} >
                                        <div className="teaser-league-container">
                                            <div>
                                                <span>{league.name}</span>
                                            </div>
                                            <input type="checkbox" className="input-teaser" checked={isChecked} readOnly />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}


            {showLimitAlert && (
                <InfoModal
                    message={errorMessages}
                    onClose={handleCloseLimitAlert}
                />
            )}

            <div className="modal fade" id="odds" data-bs-backdrop="static" data-bs-keyboard="false" tabIndex={-1} aria-hidden="true">
                <div className="modal-dialog spacing-modal">
                    <div className="modal-content content-modal-spacin">
                        <div className="modal-title-acount">
                            <span className="font-account teko">Teaser:<span className="">{ }</span></span>
                        </div>
                        <div className="container-account">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th># of Picks</th>
                                        <th># of Picks Won</th>
                                        <th>Payout</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {oddsTeaser.map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.team}</td>
                                            <td>{item.team}</td>
                                            <td>{formatNumbers(item.payout)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-danger btn-sm" data-bs-dismiss="modal">Close</button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default TeaserMenu;
